export interface MulticastTierEndpoint {
	sourceAddress?: string;
	groupAddress: string;
	port: number;
	tracks: { name: string; packetId?: number }[];
	bandwidth?: number;
	protocol?: string;
	/** False when every track on this endpoint is a `fec-repair` track, so the
	 *  endpoint carries repair symbols only and no media. A repair-layer
	 *  endpoint is joined by the loss-driven layer policy, never by the
	 *  bandwidth ladder: selecting one as a tier would leave the receiver with
	 *  no media at all. Set by the catalog policy, which resolves repair-track
	 *  membership from `depends`/`fec.repairTrack` rather than from names.
	 *  Undefined means "not classified" and is treated as carrying media, so a
	 *  caller that builds endpoints without the policy is unaffected. */
	carriesMedia?: boolean;
}

export function endpointKey(endpoint: MulticastTierEndpoint): string {
	return `${endpoint.sourceAddress ?? ""}|${endpoint.groupAddress}|${endpoint.port}`;
}

/**
 * Whether this endpoint may be selected as a media tier.
 *
 * Every path that picks a tier must apply this, not only `selectMulticastTier`:
 * the orchestrator's headroom up-selection ranks endpoints itself, because it
 * advances one adjacent rung rather than fitting a bandwidth budget. Exported
 * so both rankings share one definition of "carries media" instead of
 * repeating the comparison and drifting apart.
 */
export function carriesMedia(endpoint: MulticastTierEndpoint): boolean {
	return endpoint.carriesMedia !== false;
}

export function selectMulticastTier(
	endpoints: readonly MulticastTierEndpoint[],
	estimate: number | undefined,
): MulticastTierEndpoint | undefined {
	// Repair-only endpoints are never a media tier. Filtering once here covers
	// every return path below, including the two early returns and the fallback:
	// a ladder that has only repair-layer endpoints returns undefined rather
	// than joining one and receiving no media.
	const media = endpoints.filter(carriesMedia);
	if (media.length <= 1) return media[0];

	const ranked = media
		.filter((endpoint) => typeof endpoint.bandwidth === "number" && endpoint.bandwidth > 0)
		.sort((a, b) => a.bandwidth! - b.bandwidth!);
	if (ranked.length < 2) return media[0];
	if (estimate === undefined) return ranked[0];

	const budget = estimate * 0.8;
	let selected: MulticastTierEndpoint | undefined;
	for (const endpoint of ranked) {
		if (endpoint.bandwidth! <= budget) selected = endpoint;
	}
	return selected ?? ranked[0];
}
